import React, { useState, useMemo, useRef, useEffect } from 'react';

const FilterDropdown = ({ label, options, selectedOptions, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    } else {
      setSearchTerm('');
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return options.filter(([option]) => 
      option.toLowerCase().includes(lowerSearchTerm)
    );
  }, [options, searchTerm]);

  const [dropdownStyle, setDropdownStyle] = useState({});

  const updateDropdownPosition = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const position = spaceBelow >= 320 || spaceBelow > spaceAbove
        ? { top: rect.height + 4 }
        : { bottom: rect.height + 4 };

      setDropdownStyle({
        ...position,
        width: rect.width,
        position: 'absolute',
        left: 0,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isOpen]);

  return (
    <div className="filter-dropdown" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="filter-button"
      >
        {label} ({selectedOptions.length || 'All'})
      </button>
      {isOpen && (
        <div className="filter-options" style={dropdownStyle}>
          <div className="filter-search">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {filteredOptions.map(([option, count]) => (
            <label key={option} className="filter-option">
              <input
                type="checkbox"
                checked={selectedOptions.includes(option)}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange([...selectedOptions, option]);
                  } else {
                    onChange(selectedOptions.filter(item => item !== option));
                  }
                }}
              />
              <span className="filter-option-text">{option}</span>
              <span className="filter-option-count">{count}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const MCPServerTable = ({ servers }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'github_stars',
    direction: 'desc'
  });

  // Get unique values and their counts for each filterable column
  const filterOptions = useMemo(() => {
    const getCounts = (field) => {
      const counts = servers.reduce((acc, server) => {
        if (server[field]) {
          acc[server[field]] = (acc[server[field]] || 0) + 1;
        }
        return acc;
      }, {});
      
      // Convert to array of [value, count] pairs and sort by count
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    };

    return {
      package_registry: getCounts('package_registry')
    };
  }, [servers]);

  // State for temporary filter selections
  const [selectedFilters, setSelectedFilters] = useState({
    package_registry: []
  });

  // State for active filters
  const [activeFilters, setActiveFilters] = useState({
    package_registry: []
  });

  const handleApplyFilters = () => {
    setActiveFilters(selectedFilters);
  };

  const handleResetFilters = () => {
    setSelectedFilters({
      package_registry: []
    });
    setActiveFilters({
      package_registry: []
    });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return '▼';
    }
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  const getSortDirection = (columnName) => {
    if (sortConfig.key === columnName) {
      return sortConfig.direction === 'asc' ? 'ascending' : 'descending';
    }
    return 'none';
  };

  // Filter and sort servers
  const filteredAndSortedServers = useMemo(() => {
    // First apply search
    let result = servers;
    
    if (searchTerm.trim() !== '') {
      const lowerSearchTerm = searchTerm.toLowerCase();
      result = result.filter(server => {
        // Search across all text fields
        return Object.entries(server).some(([key, value]) => {
          if (typeof value === 'string') {
            return value.toLowerCase().includes(lowerSearchTerm);
          } else if (typeof value === 'number') {
            return value.toString().includes(lowerSearchTerm);
          }
          return false;
        });
      });
    }
    
    // Then apply filters
    result = result.filter(server => {
      return Object.entries(activeFilters).every(([key, selectedValues]) => {
        // If no filters are selected for this key, include all servers
        if (selectedValues.length === 0) return true;
        // Check if the server's value for this key is in the selected values
        return selectedValues.includes(server[key]);
      });
    });

    // Then apply sorting
    if (!sortConfig.key) return result;

    return [...result].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle special sorting for different columns
      if (sortConfig.key === 'released_on') {
        // Convert dates to timestamps for comparison
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (sortConfig.key === 'github_stars' || sortConfig.key === 'package_download_count') {
        // Convert to numbers for numeric comparison
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      } else if (typeof aValue === 'string') {
        // Case-insensitive string comparison
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      // Compare values
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      
      // If values are equal, sort by server name as secondary sort
      if (sortConfig.key !== 'server_name') {
        const aName = a.server_name.toLowerCase();
        const bName = b.server_name.toLowerCase();
        return aName.localeCompare(bName);
      }
      
      return 0;
    });
  }, [servers, searchTerm, sortConfig, activeFilters]);

  return (
    <div className="mcp-table-container">
      <div className="filters-section">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search servers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="table-search-input"
          />
        </div>
        <div className="filter-container">
          <FilterDropdown
            label="Package Registry"
            options={filterOptions.package_registry}
            selectedOptions={selectedFilters.package_registry}
            onChange={(selected) => setSelectedFilters(prev => ({ ...prev, package_registry: selected }))}
          />
        </div>
        <div className="filter-actions">
          <button 
            className="filter-apply-button"
            onClick={handleApplyFilters}
          >
            Apply Filters
          </button>
          <button 
            className="filter-reset-button"
            onClick={handleResetFilters}
          >
            Reset
          </button>
        </div>
        {Object.values(activeFilters).some(filters => filters.length > 0) && (
          <div className="active-filters">
            Active filters:
            {Object.entries(activeFilters).map(([key, values]) => 
              values.length > 0 && (
                <span key={key} className="filter-tag">
                  {key}: {values.join(', ')}
                </span>
              )
            )}
          </div>
        )}
      </div>
      <table className="mcp-table">
        <thead>
          <tr>
            <th 
              onClick={() => handleSort('server_name')} 
              className="sortable"
              data-sort-icon={getSortIcon('server_name')}
              aria-sort={getSortDirection('server_name')}
              title="Sort by repository"
            >
              GitHub User / Repository Name
            </th>
            <th 
              onClick={() => handleSort('github_stars')} 
              className="sortable"
              data-sort-icon={getSortIcon('github_stars')}
              aria-sort={getSortDirection('github_stars')}
              title="Sort by github_stars"
            >
              GitHub Stars
            </th>
            <th 
              onClick={() => handleSort('package_registry')} 
              className="sortable"
              data-sort-icon={getSortIcon('package_registry')}
              aria-sort={getSortDirection('package_registry')}
              title="Sort by package_registry"
            >
              Package Registry
            </th>
            <th 
              onClick={() => handleSort('package_name')} 
              className="sortable"
              data-sort-icon={getSortIcon('package_name')}
              aria-sort={getSortDirection('package_name')}
              title="Sort by package_name"
            >
              Package Name
            </th>
            <th 
              onClick={() => handleSort('package_download_count')} 
              className="sortable"
              data-sort-icon={getSortIcon('package_download_count')}
              aria-sort={getSortDirection('package_download_count')}
              title="Sort by package_download_count"
            >
              Downloads
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredAndSortedServers.map((server, index) => (
            <tr key={index}>
              <td>
                {server.url && server.url.includes('github.com') && (
                  <div className="table-github-info">
                    <img 
                      src={`https://github.com/${server.url.match(/github\.com\/([^/]+)/)[1]}.png`}
                      alt="GitHub Avatar"
                      className="table-github-avatar"
                      onError={(e) => {
                        e.target.src = 'https://github.githubassets.com/images/modules/logos_page/GitHub-Mark.png';
                      }}
                    />
                    <div>
                      <a 
                        href={server.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="table-github-link"
                      >
                        {server.server_name}
                      </a>
                      <p className="table-description">{server.description}</p>
                    </div>
                  </div>
                )}
                {(!server.url || !server.url.includes('github.com')) && (
                  <div className="table-info">
                    <span className="table-server-name">{server.server_name}</span>
                    <p className="table-description">{server.description}</p>
                  </div>
                )}
              </td>
              <td>
                {server.github_stars > 0 && (
                  <div className="table-stat">
                    <span className="stat-icon">⭐</span>
                    <span>{server.github_stars}</span>
                  </div>
                )}
              </td>
              <td>{server.package_registry}</td>
              <td>{server.package_name}</td>
              <td>
                {server.package_download_count > 0 && (
                  <div className="table-stat">
                    <span className="stat-icon">📥</span>
                    <span>{server.package_download_count}</span>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default MCPServerTable;
